import { Body, H2, H3 } from '@casavo/habitat'
import cn from 'classnames'
import React, { ReactNode } from 'react'

import { CasavoAdvantage } from '@/containers/ListingDetail/CasavoAdvantage'
import { useDevice } from '@/context/Device'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import { TENANTS } from '@/lib/tenants/constants'
import { Tenants } from '@/lib/tenants/types'
import { baseRadius } from '@/shared-styles/common-variables'
import { backgroundColor } from '@/shared-styles/styles.css'
import { CasavoInventory } from '@/types/listing'

import { ItemsCarousel } from '../ItemsCarousel/ItemsCarousel'
import { Translated } from '../Translated'

import { AdvantageCard } from './AdvantageCard'
import { getDescription, getThisIsACasavoClaimLabel, getTitle } from './i18nKeys'
import { bannerDescription, header, headerImage } from './styles.css'
import { ThisIsACasavoClaim } from './ThisIsACasavoClaim'

const titleTenantTypography: Record<Tenants, typeof H2 | typeof H3> = {
  [TENANTS.casavo]: H2,
  [TENANTS.ucsc]: H3,
}

export const InventoryBannerBig: React.FC<{
  advantages: ReadonlyArray<CasavoAdvantage>
  color?: keyof typeof backgroundColor
  hasContentBottom?: boolean
  inventory: CasavoInventory
  tenant?: Tenants
}> = ({ advantages, color = 'offWhite', hasContentBottom, inventory, tenant = TENANTS.casavo }) => {
  const TitleTypography = titleTenantTypography[tenant]

  return (
    <div
      className={cn(
        header,
        tenant === TENANTS.casavo ? headerImage : {},
        backgroundColor[color],
        sprinkles({
          borderRadius: !hasContentBottom && baseRadius,
          marginBottom: !hasContentBottom ? '3xl' : 0,
          padding: 'xl',
        })
      )}
      data-testid={'inventory-big-banner-header'}
    >
      <TitleTypography>
        <Translated i18Key={getTitle(inventory)} />
      </TitleTypography>
      <div className={bannerDescription}>
        <Body noMargin size="s">
          <Translated i18Key={getDescription(inventory, tenant)} />
        </Body>
      </div>
      {tenant === TENANTS.casavo && <ThisIsACasavoClaim i18nKey={getThisIsACasavoClaimLabel(inventory)} />}
      <AdvantageContainer tenant={tenant}>
        {advantages.map((advantage, index) => (
          <AdvantageCard key={index} advantage={advantage} />
        ))}
      </AdvantageContainer>
    </div>
  )
}

const AdvantageContainer: React.FC<{ children: ReactNode[]; tenant: Tenants }> = ({ children, tenant }) => {
  const device = useDevice()
  if (device === 'desktop' && tenant === TENANTS.ucsc) {
    return (
      <div style={{ display: 'flex', gap: theme.spacings['m'] }}>
        {React.Children.map(children, (child, index) => (
          <div key={index} style={{ flexBasis: `${100 / children.length}%` }}>
            {child}
          </div>
        ))}
      </div>
    )
  }
  return (
    <ItemsCarousel itemsGap={'m'} itemsWidth={265}>
      {children}
    </ItemsCarousel>
  )
}

export default InventoryBannerBig
